import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BadgeCheck, CircleCheck, Clock, Copy, CreditCard, FileText, LockKeyhole,
  Mail, MapPin, ReceiptText, Send, ShieldCheck, TriangleAlert, Video,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import DealHeader from '@/components/deal/DealHeader';
import ProposalStatusBadge from '@/components/deal/ProposalStatusBadge';
import useDealFlow from '@/hooks/useDealFlow';
import {
  AGREEMENT_PAYMENT_STATUSES,
  DEAL_STAGES_V2,
  formatDollars,
  getCurrentCreativeForBusiness,
  getLatestProposalForBusiness,
  isMockGenerationEnabled,
} from '@/components/deal/mockDealData';

export default function AcceptClose() {
  const { orgSlug, dealId } = useParams();
  const navigate = useNavigate();
  const deal = useDealFlow();
  const mockGenerationEnabled = isMockGenerationEnabled();
  const businessId = deal.activeBusinessId || deal.businessProfile?.id;
  const proposal = deal.proposal || getLatestProposalForBusiness(deal.proposals?.[businessId] || []);
  const creativePreview = useMemo(
    () => getCurrentCreativeForBusiness(deal, businessId, { mockGenerationEnabled }),
    [deal, businessId, mockGenerationEnabled],
  );
  const blockersPreview = deal.launchReadinessByBusiness?.[businessId] || { blockers: [] };
  const [termsAccepted, setTermsAccepted] = useState(!!deal.agreement?.accepted_at);
  const [paymentMode, setPaymentMode] = useState(deal.agreement?.billing_mode === 'card' ? 'collect' : 'link');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    deal.setCurrentStage('close');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!businessId) return;
    if (deal.agreement?.proposalId === proposal?.id) return;
    deal.loadWorkspaceAgreementForBusiness(businessId).catch(() => {});
  }, [businessId, deal.agreement?.proposalId, deal.loadWorkspaceAgreementForBusiness, proposal?.id]);

  if (!proposal) {
    return (
      <>
        <DealHeader
          businessName={deal.businessProfile?.name}
          currentStage="close"
          stages={DEAL_STAGES_V2}
          onBack={() => navigate(-1)}
        />
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6 text-center">
              <h1 className="text-2xl font-semibold text-slate-950">Agreement & billing</h1>
              <p className="mt-2 text-sm text-slate-600">
                A proposal must exist before billing can be configured.
              </p>
              <Button onClick={() => navigate(`/org/${orgSlug}/deal/${dealId}/proposal`)} className="mt-5 rounded-xl bg-coral text-white hover:bg-coral/90">
                Open proposal
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const paymentStatus = deal.agreement?.payment_method_status || 'not_started';
  const paymentStatusConfig = AGREEMENT_PAYMENT_STATUSES[paymentStatus] || AGREEMENT_PAYMENT_STATUSES.not_started;
  const paymentUrl = deal.agreement?.payment_link_url || `https://cornercast.tv/pay/${proposal?.id || dealId}`;
  const proposalAccepted = proposal?.status === 'accepted';
  const isCardValid = cardNumber.replace(/\s/g, '').length >= 15 && expiry.length >= 4 && cvc.length >= 3;
  const primaryBlocker = !proposalAccepted ? 'Proposal must be accepted before billing can be finalized.' : null;

  const copiedTimeoutRef = useRef(null);
  const handleCopy = () => {
    navigator.clipboard?.writeText(paymentUrl);
    setCopied(true);
    toast.success('Billing link copied');
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => () => {
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
  }, []);

  const handleSave = async () => {
    if (!termsAccepted) {
      toast.error('Confirm the agreement before continuing.');
      return;
    }
    if (!proposalAccepted) {
      toast.error('Record proposal acceptance before setting up billing.');
      return;
    }
    if (paymentMode === 'collect' && !isCardValid) {
      toast.error('Enter a valid payment method before continuing.');
      return;
    }

    try {
      let agreement = deal.agreement;
      if (!agreement?.id) {
        agreement = await deal.createAgreementForProposal(businessId, proposal.id);
      }

      if (paymentMode === 'link') {
        await deal.startBillingForAgreement(businessId, agreement.id, 'payment_link');
        toast.success('Billing link ready to share');
      } else {
        const startedAgreement = await deal.startBillingForAgreement(businessId, agreement.id, 'card');
        await deal.confirmBillingForAgreement(businessId, startedAgreement.id, {
          external_reference: 'manual_card_collection',
          metadata_json: {
            last4: cardNumber.slice(-4),
            expiry,
          },
        });
        toast.success('Billing confirmed');
      }

      navigate(`/org/${orgSlug}/deal/${dealId}/launch`);
    } catch (error) {
      console.error('Failed to save billing state', error);
      toast.error(error?.message || 'Could not update billing');
    }
  };

  return (
    <>
      <DealHeader
        businessName={deal.businessProfile?.name}
        currentStage="close"
        stages={DEAL_STAGES_V2}
        onBack={() => navigate(-1)}
      />

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">Agreement & billing</h1>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.35fr_0.95fr]">
          <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div>
                  <div className="flex items-center gap-2">
                    <ProposalStatusBadge status={proposal?.status || 'draft'} />
                    <Badge variant="outline" className="rounded-full bg-slate-50 text-xs text-slate-600">
                      v{proposal?.version || 1}
                    </Badge>
                  </div>
                  <h2 className="mt-3 text-2xl font-bold text-slate-950">{proposal?.packageName || deal.selectedPackage?.name}</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    {deal.businessProfile?.name} · {formatDollars(proposal?.price || deal.selectedPackage?.pricing?.sellPrice || deal.selectedPackage?.budget || 0)}
                  </p>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-5">
                  <div>
                    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                      <ReceiptText className="h-3.5 w-3.5 text-slate-500" />
                      Package
                    </div>
                    <p className="mt-1 text-sm font-semibold text-slate-950">{proposal?.packageName || deal.selectedPackage?.name}</p>
                  </div>
                  <div className="h-8 w-px bg-slate-200" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                      <MapPin className="h-3.5 w-3.5 text-coral" />
                      Targeting
                    </div>
                    <p className="mt-1 text-sm font-semibold text-slate-950 truncate">{proposal?.target_area?.label || deal.selectedPackage?.targetAddress}</p>
                  </div>
                  <div className="h-8 w-px bg-slate-200" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                      <Video className="h-3.5 w-3.5 text-blue-600" />
                      Creative
                    </div>
                    <p className="mt-1 text-sm font-semibold text-slate-950 truncate">
                      {creativePreview.type === 'none' ? 'No creative attached' : creativePreview.label}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {primaryBlocker && (
              <Card className="border-amber-200 bg-amber-50 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <TriangleAlert className="mt-0.5 h-5 w-5 text-amber-700" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-amber-900">Commercial blocker</p>
                      <p className="mt-1 text-sm text-amber-800">{primaryBlocker}</p>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/org/${orgSlug}/deal/${dealId}/proposal`)} className="mt-3 rounded-xl bg-white">
                        <FileText className="h-4 w-4" />
                        Open proposal
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-teal" />
                  <h3 className="text-base font-semibold text-slate-950">Agreement confirmation</h3>
                </div>
                <label className="mt-4 flex items-start gap-3 rounded-2xl border border-slate-200 p-4">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(event) => setTermsAccepted(event.target.checked)}
                    className="mt-1 accent-coral"
                  />
                  <span className="text-sm text-slate-700">
                    I confirm the buyer accepted the commercial terms for this proposal.
                  </span>
                </label>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-coral" />
                  <h3 className="text-base font-semibold text-slate-950">Billing setup</h3>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setPaymentMode('link')}
                    className={`rounded-2xl border px-4 py-4 text-left transition-colors ${
                      paymentMode === 'link' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700'
                    }`}
                  >
                    <Send className="mb-2 h-4 w-4" />
                    <p className="text-sm font-semibold">Send billing link</p>
                    <p className={`mt-1 text-sm ${paymentMode === 'link' ? 'text-white/75' : 'text-slate-500'}`}>
                      Share a secure billing link for the buyer to complete later.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMode('collect')}
                    className={`rounded-2xl border px-4 py-4 text-left transition-colors ${
                      paymentMode === 'collect' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700'
                    }`}
                  >
                    <CreditCard className="mb-2 h-4 w-4" />
                    <p className="text-sm font-semibold">Collect billing in person</p>
                    <p className={`mt-1 text-sm ${paymentMode === 'collect' ? 'text-white/75' : 'text-slate-500'}`}>
                      Have the buyer enter payment details while you are together.
                    </p>
                  </button>
                </div>

                {paymentMode === 'link' ? (
                  <div className="mt-5 rounded-2xl border border-slate-200 p-4">
                    <Label className="text-xs text-slate-500">Billing link</Label>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                      <Input value={paymentUrl} readOnly className="rounded-xl border-slate-200 bg-slate-50" />
                      <Button variant="outline" onClick={handleCopy} className="shrink-0 rounded-xl bg-white">
                        {copied ? <CircleCheck className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                      <LockKeyhole className="h-4 w-4" />
                      Billing is collected separately from proposal approval.
                    </p>
                  </div>
                ) : (
                  <div className="mt-5 rounded-2xl border border-slate-200 p-4">
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-slate-500">Card number</Label>
                        <Input
                          value={cardNumber}
                          onChange={(event) => setCardNumber(event.target.value.replace(/\D/g, '').slice(0, 16))}
                          className="mt-1 rounded-xl border-slate-200 bg-slate-50"
                          placeholder="4242 4242 4242 4242"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">Expiry</Label>
                          <Input
                            value={expiry}
                            onChange={(event) => setExpiry(event.target.value.slice(0, 5))}
                            className="mt-1 rounded-xl border-slate-200 bg-slate-50"
                            placeholder="MM/YY"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">CVC</Label>
                          <Input
                            value={cvc}
                            onChange={(event) => setCvc(event.target.value.replace(/\D/g, '').slice(0, 4))}
                            className="mt-1 rounded-xl border-slate-200 bg-slate-50"
                            placeholder="123"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-base font-semibold text-slate-950">Billing status</h3>
                  <Badge variant="outline" className={`rounded-full text-xs ${paymentStatusConfig.className}`}>
                    {paymentStatusConfig.label}
                  </Badge>
                </div>

                <div className="mt-5 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-950">
                        {termsAccepted ? 'Buyer accepted' : "Waiting on buyer's reply"}
                      </p>
                      <p className="mt-0.5 text-sm text-slate-500">Agreement</p>
                    </div>
                    {termsAccepted ? <CircleCheck className="h-4 w-4 shrink-0 text-emerald-500" /> : <Clock className="h-4 w-4 shrink-0 text-slate-400" />}
                  </div>
                  <div className="h-px bg-slate-100" />
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-950">
                        {paymentMode === 'collect' ? 'Collect in person' : 'Billing link'}
                      </p>
                      <p className="mt-0.5 text-sm text-slate-500">Billing method</p>
                    </div>
                    <CreditCard className="h-4 w-4 shrink-0 text-slate-400" />
                  </div>
                  <div className="h-px bg-slate-100" />
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-950">
                        {proposalAccepted ? 'Ready to launch' : "Buyer hasn't accepted yet"}
                      </p>
                      <p className="mt-0.5 text-sm text-slate-500">Needed before launch</p>
                    </div>
                    {proposalAccepted ? <CircleCheck className="h-4 w-4 shrink-0 text-emerald-500" /> : <TriangleAlert className="h-4 w-4 shrink-0 text-amber-500" />}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-2">
                  <BadgeCheck className="h-4 w-4 text-emerald-500" />
                  <h3 className="text-base font-semibold text-slate-950">Launch readiness preview</h3>
                </div>
                <div className="mt-4 space-y-4">
                  {blockersPreview.blockers.slice(0, 3).map((blocker, i) => (
                    <React.Fragment key={blocker.key}>
                      {i > 0 && <div className="h-px bg-slate-100" />}
                      <div className="flex items-start gap-3">
                        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-950">{blocker.label}</p>
                          <p className="mt-0.5 text-sm text-slate-500">{blocker.detail}</p>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={handleSave}
              disabled={!termsAccepted || !proposalAccepted || (paymentMode === 'collect' && !isCardValid)}
              className="h-12 w-full rounded-xl bg-coral text-white hover:bg-coral/90"
            >
              {paymentMode === 'collect' ? (
                <>
                  <CreditCard className="h-4 w-4" />
                  Confirm billing
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  Save billing link setup
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
